import React, { useEffect, useState } from "react";
import { getExpenseForecastComparision } from "../../utils/ActionCreator";

function Skeleton({ width = "100%", height = "10px", radius = "6px" }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: radius,
        background:
          "linear-gradient(90deg, rgba(3,107,101,0.06) 25%, rgba(3,107,101,0.13) 50%, rgba(3,107,101,0.06) 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
      }}
    />
  );
}

function BarRow({ item, index, animated }) {
  const [hovered, setHovered] = useState(false);
  const isPercent = item.type === "percent";

  const gradients = {
    forecast: "linear-gradient(90deg, #616262, #b9b9ce)",
    actual: "linear-gradient(90deg, #036B65AA, #7adbd6)",
    percent:
      "linear-gradient(90deg, rgba(48, 49, 49, 0.3), rgba(3,107,101,0.55))",
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "10px 14px",
        borderRadius: "14px",
        background: hovered ? "rgba(3,107,101,0.04)" : "transparent",
        border: `1px solid ${hovered ? "rgba(3,107,101,0.12)" : "transparent"}`,
        transition: "all 0.25s ease",
        animation: `fadeSlideIn 0.5s ease both`,
        animationDelay: `${index * 0.13 + 0.2}s`,
        cursor: "default",
      }}
    >
      <div style={{ width: "72px", flexShrink: 0, textAlign: "right" }}>
        <span
          style={{
            fontSize: "11px",
            fontWeight: "700",
            color: hovered ? "#036B65" : "#1a4a47",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            transition: "color 0.2s ease",
          }}
        >
          {item.label}
        </span>
      </div>

      <div
        style={{
          position: "relative",
          flex: 1,
          height: "28px",
          background: "rgba(3,107,101,0.07)",
          borderRadius: "8px",
          overflow: "visible",
        }}
      >
        {[25, 50, 75].map((g) => (
          <div
            key={g}
            style={{
              position: "absolute",
              left: `${g}%`,
              top: 0,
              bottom: 0,
              width: "1px",
              background: "rgba(3,107,101,0.08)",
              zIndex: 1,
            }}
          />
        ))}

        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            width: animated ? `${item.pct}%` : "0%",
            background: gradients[item.type],
            borderRadius: "8px",
            transition: `width 1.1s cubic-bezier(0.4,0,0.2,1) ${index * 0.15}s`,
            boxShadow:
              hovered && !isPercent ? "0 2px 12px rgba(3,107,101,0.3)" : "none",
            overflow: "hidden",
          }}
        >
          {hovered && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.2s infinite",
              }}
            />
          )}

          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "35%",
              background: "rgba(255,255,255,0.12)",
              borderRadius: "8px 8px 0 0",
            }}
          />
        </div>
      </div>

      <div style={{ width: "80px", flexShrink: 0, textAlign: "left" }}>
        <span
          style={{
            fontSize: isPercent ? "18px" : "15px",
            color: isPercent ? "#036B65" : "#0d2b29",
            letterSpacing: "-0.5px",
            transition: "transform 0.2s ease, color 0.2s ease",
            transform: hovered ? "scale(1.06)" : "scale(1)",
            display: "inline-block",
          }}
        >
          {isPercent
            ? `${item.value}%`
            : `₹${item.value.toLocaleString("en-IN")}`}
        </span>
      </div>
    </div>
  );
}

export default function ProgressGraph({ dateFilter }) {
  const [expenseData, setExpenseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animated, setAnimated] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (!dateFilter?.from || !dateFilter?.to) return;
    setLoading(true);
    setAnimated(false);

    getExpenseForecastComparision(
      dateFilter.from,
      dateFilter.to,
      (data) => {
        const totalForecast = data.reduce(
          (sum, item) => sum + (item.foreCastAmount || 0),
          0,
        );
        const totalActual = data.reduce(
          (sum, item) => sum + (item.amount || 0),
          0,
        );

        setExpenseData({ foreCastAmount: totalForecast, amount: totalActual });
        setLastUpdated(new Date());
        setLoading(false);
        setTimeout(() => setAnimated(true), 80);
      },
      (error) => {
        console.error(error);
        setLoading(false);
      },
    );
  }, [dateFilter]); // ✅ Re-fetches automatically when parent dateFilter changes

  const forecast = expenseData?.foreCastAmount || 0;
  const actual = expenseData?.amount || 0;
  const progress = forecast
    ? Math.min(Math.round((actual / forecast) * 100), 100)
    : 0;

  const rows = expenseData
    ? [
        { id: 1, label: "Forecast", value: forecast, pct: 100, type: "forecast" },
        {
          id: 2,
          label: "Actual",
          value: actual,
          pct: forecast ? Math.min((actual / forecast) * 100, 100) : 0,
          type: "actual",
        },
        { id: 3, label: "Progress", value: progress, pct: progress, type: "percent" },
      ]
    : [];

  const now = lastUpdated?.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <>
      <style>{`
        @keyframes shimmer { from{background-position:200% 0} to{background-position:-200% 0} }
        @keyframes fadeSlideIn { from{opacity:0;transform:translateX(-12px)} to{opacity:1;transform:translateX(0)} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes pulseOpacity { 0%,100%{opacity:1} 50%{opacity:0.4} }

        .ep-container {
          background: #fff;
          padding: 20px;
          border-radius: 12px;
          border: 1px solid #E5E7EB;
          height: 100%;
          width: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-sizing: border-box;
        }

        .ep-header {
          display: flex;
          align-items: center;
          gap: 9px;
          margin-bottom: 15px;
          flex-shrink: 0;
        }

        .ep-header-indicator {
          width: 8px;
          height: 8px;
          min-width: 8px;
          border-radius: 50%;
          background: #036B65;
          box-shadow: 0 0 0 3px rgba(3,107,101,0.2);
        }

        .ep-title {
          margin: 0;
          font-size: 17px;
          font-weight: 700;
          color: #0d2b29;
          letter-spacing: -0.3px;
          white-space: nowrap;
        }

        .ep-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-height: 0;
          overflow: hidden;
        }

        .ep-footer {
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid rgba(3,107,101,0.07);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }
      `}</style>

      <div
        style={{
          fontFamily: "'DM Sans','Segoe UI',sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <div className="ep-container">
          {/* Header — no date inputs, just the title */}
          <div className="ep-header">
            <div
              className="ep-header-indicator"
              style={{
                animation: loading ? "pulseOpacity 1.5s infinite" : "none",
              }}
            />
            <h2 className="ep-title">Expense Progress</h2>
          </div>

          {!loading && (
            <div
              style={{
                display: "flex",
                marginLeft: "86px",
                marginRight: "94px",
                marginBottom: "6px",
                flexShrink: 0,
              }}
            >
              {[0, 25, 50, 75, 100].map((v) => (
                <div
                  key={v}
                  style={{
                    flex: v === 0 ? 0 : 1,
                    textAlign: v === 0 ? "left" : "right",
                  }}
                >
                  <span
                    style={{
                      fontSize: "9px",
                      color: "#aacfcd",
                      fontWeight: "600",
                    }}
                  >
                    {v}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="ep-content">
            {loading
              ? [0, 1, 2].map((i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                      padding: "10px 14px",
                    }}
                  >
                    <Skeleton width="72px" height="12px" />
                    <div style={{ flex: 1 }}>
                      <Skeleton width="100%" height="28px" radius="8px" />
                    </div>
                    <Skeleton width="60px" height="14px" />
                  </div>
                ))
              : rows.map((item, i) => (
                  <BarRow
                    key={item.id}
                    item={item}
                    index={i}
                    animated={animated}
                  />
                ))}
          </div>

          <div className="ep-footer">
            <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
              {[
                { color: "linear-gradient(90deg,#b9b9ce,#101313)", label: "Forecast" },
                { color: "linear-gradient(90deg,#036B65AA,#7adbd6)", label: "Actual" },
                {
                  color: "linear-gradient(90deg,rgba(3,107,101,0.3),rgba(3,107,101,0.55))",
                  label: "Progress",
                },
              ].map((leg) => (
                <div
                  key={leg.label}
                  style={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  <div
                    style={{
                      width: "14px",
                      height: "8px",
                      borderRadius: "3px",
                      background: leg.color,
                    }}
                  />
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#7aaca8",
                      fontWeight: "500",
                    }}
                  >
                    {leg.label}
                  </span>
                </div>
              ))}
            </div>
            {now && (
              <span style={{ fontSize: "10px", color: "#b0d4d2", fontWeight: "500" }}>
                {/* Updated {now} */}
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}